import React, { useMemo, useState } from 'react';
import { Routes, Route, Link, Navigate } from 'react-router-dom';
import UserManagement from './UserManagement';
import ProcessorsConsole from './processors/ProcessorsConsole';
import DataExport from './DataExport';
import LogViewer from './LogViewer';
import ValifiCreditCheck from './ValifiCreditCheck';
import PalantirLLMTest from './PalantirLLMTest';
import AdminEmailTemplates from './AdminEmailTemplates';
import MemoBroadcast from './MemoBroadcast';
import LOASenderManagement from './LOASenderManagement';
import AdminFoaRegeneration from './AdminFoaRegeneration';
import AdminLocResendBatch from './AdminLocResendBatch';
import AdminLoaRequests from './AdminLoaRequests';
import AdminRedressStats from './AdminRedressStats';
import RedressDashboard from '../pages/redress/RedressDashboard';
import FosReferralsList from '../pages/redress/FosReferralsList';
import AdminBacklog from './AdminBacklog';
import LenderResponseImport from './LenderResponseImport';
import LenderEmailFilter from './LenderEmailFilter';
import { useProcessorStates } from './ProcessorStateBadge';
import { Card, SearchInput, PageHeader, StatusPill } from './ui';
import './AdminDashboard.css';

/** Registry of admin tools - drives both the hub cards and the routes. */
export interface AdminTool {
  path: string;
  label: string;
  description: string;
  element: React.ReactElement;
}

export const adminTools: AdminTool[] = [
  { path: 'processors', label: 'Processors', description: 'Control and monitor the background processors', element: <ProcessorsConsole /> },
  { path: 'users', label: 'User Management', description: 'Manage user accounts, roles, and permissions', element: <UserManagement /> },
  { path: 'logs', label: 'System Logs', description: 'View and search system logs (production only)', element: <LogViewer /> },
  { path: 'export', label: 'Data Export', description: 'Export claims data in various formats', element: <DataExport /> },
  { path: 'credit-check', label: 'Credit Check', description: 'Run Valifi credit checks to find motor finance agreements', element: <ValifiCreditCheck /> },
  { path: 'llm-test', label: 'LLM Test', description: 'Test LLM providers with custom prompts', element: <PalantirLLMTest /> },
  { path: 'email-templates', label: 'Email Catalogue', description: 'Manage reusable email settings, mappings and revision history', element: <AdminEmailTemplates /> },
  { path: 'memo-broadcast', label: 'Memo Broadcast', description: 'Send custom memo correspondence to live claims by lender', element: <MemoBroadcast /> },
  { path: 'loa-sender', label: 'LOA Sender', description: 'Bulk send LOA questionnaire links and track coverage', element: <LOASenderManagement /> },
  { path: 'foa-regeneration', label: 'FOA Regeneration', description: 'Regenerate Form of Authority documents', element: <AdminFoaRegeneration /> },
  { path: 'loc-resend', label: 'Resend Letters to Lenders', description: 'Resend corrected letters of claim to lenders only - no client emails', element: <AdminLocResendBatch /> },
  { path: 'loa-requests', label: 'LOA requests', description: 'Ask clients who have not signed an LOA to sign one, a few each day', element: <AdminLoaRequests /> },
  { path: 'redress-stats', label: 'Redress Stats', description: 'Redress scheme eligibility breakdown', element: <AdminRedressStats /> },
  { path: 'redress-dashboard', label: 'Redress Dashboard', description: 'Firm-wide redress case status, financials and ageing', element: <RedressDashboard /> },
  { path: 'redress-fos-referrals', label: 'FOS Referrals', description: 'Track FOS referral cases', element: <FosReferralsList /> },
  { path: 'backlog', label: 'CAF Backlog', description: 'Segregated CAF pre-processor backlog - triage, promote, archive', element: <AdminBacklog /> },
  { path: 'lender-response-import', label: 'Lender Response Import', description: 'Import a lender\'s returned spreadsheet onto the matched claims (data only)', element: <LenderResponseImport /> },
];

const AdminDashboard: React.FC = () => {
  const [showLenderEmailFilter, setShowLenderEmailFilter] = useState(false);

  return (
    <div className="admin-dashboard">
      <Routes>
        <Route path="/" element={<AdminHub onOpenLenderEmailFilter={() => setShowLenderEmailFilter(true)} />} />
        {adminTools.map((tool) => (
          <Route
            key={tool.path}
            path={`/${tool.path}`}
            element={
              <div className="admin-tool-page">
                <PageHeader breadcrumb={{ label: 'Admin hub', to: '/admin' }} />
                {tool.element}
              </div>
            }
          />
        ))}
        <Route path="/caf-corpus" element={<Navigate to="/ai-curation" replace />} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>

      <LenderEmailFilter isOpen={showLenderEmailFilter} onClose={() => setShowLenderEmailFilter(false)} />
    </div>
  );
};

const AdminHub: React.FC<{ onOpenLenderEmailFilter: () => void }> = ({ onOpenLenderEmailFilter }) => {
  const [query, setQuery] = useState('');
  const processorStates = useProcessorStates();

  const processorsHint = useMemo(() => {
    const docs = Object.values(processorStates || {});
    if (!docs.length) return null;
    const running = docs.filter((d) => d?.actualState === 'running').length;
    const errors = docs.filter((d) => d?.actualState === 'crashed').length;
    return { running, errors };
  }, [processorStates]);

  const visibleTools = adminTools.filter((t) =>
    `${t.label} ${t.description}`.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="admin-hub">
      <PageHeader title="Admin" description="All administrative tools" />
      <div className="admin-hub__search">
        <SearchInput value={query} onChange={setQuery} placeholder="Search admin tools…" />
      </div>
      <div className="admin-hub__grid">
        {visibleTools.map((tool) => (
          <Link key={tool.path} to={`/admin/${tool.path}`} className="admin-hub__cardlink">
            <Card title={tool.label}>
              <p className="admin-hub__desc">{tool.description}</p>
              {tool.path === 'processors' && processorsHint && (
                <p className="admin-hub__hint">
                  <StatusPill status="running" label={`${processorsHint.running} running`} />{' '}
                  {processorsHint.errors > 0 && (
                    <StatusPill status="error" label={`${processorsHint.errors} error${processorsHint.errors === 1 ? '' : 's'}`} />
                  )}
                </p>
              )}
            </Card>
          </Link>
        ))}
        {'cancellation payments stripe'.includes(query.toLowerCase()) && (
          <Link to="/admin/cancellation-payments" className="admin-hub__cardlink">
            <Card title="Cancellation Payments">
              <p className="admin-hub__desc">Review Stripe payment requests and statuses</p>
            </Card>
          </Link>
        )}
        {'lender email filter'.includes(query.toLowerCase()) && (
          <button type="button" className="admin-hub__cardlink admin-hub__cardbutton" onClick={onOpenLenderEmailFilter}>
            <Card title="Lender Email Filter">
              <p className="admin-hub__desc">Manage known lender email addresses and domains for the email classifier</p>
              <p className="admin-hub__hint">Open Filter →</p>
            </Card>
          </button>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
